/*
An OpenURL consists of
- baseUrl: the address of a link-server (link resolver)
- query string: key-value pairs serializing a ContextObject.

ContextObject is a series of key-value pairs containing most often
bibliographic data and a mandatory version number for OpenUrl (Z39.88-2004)
*/

import queryString from 'query-string';
import { notUndefined as trimPropsWithUndefinedValues } from '../object';

import fieldMap from './field-map.json';
import typeGenreMap from './type-genre-map.json';

export function getDocumentType(doc) {
  const type = doc.type.toLowerCase();
  return typeGenreMap[type] ? type : 'generic';
}

export function getGenres(docType) {
  if (!typeGenreMap[docType]) return {};

  return {
    rft_val_fmt: `info:ofi/fmt:kev:mtx:${typeGenreMap[docType][0]}`,
    'rft.genre': typeGenreMap[docType][1]
  };
}

export function getTypeSpecificMaps(docType) {
  return fieldMap[docType] ? fieldMap[docType] : fieldMap.default;
}

export function getMappedFields(doc, docType) {
  const fieldMaps = {
    ...fieldMap.common,
    ...getTypeSpecificMaps(docType)
  };

  const mappedFields = Object
    .keys(fieldMaps)
    .reduce((result, key) => {
      if (doc[key]) {
        return {
          ...result,
          [fieldMaps[key]]: doc[key],
        };
      }

      return result;
    }, {});

  // for compatibility with broken resolvers (EBSCOhost) [WWW-8409]
  if (mappedFields['rft.jtitle'] !== undefined) {
    mappedFields['rft.title'] = mappedFields['rft.jtitle'];
  }

  return mappedFields;
}

const getSecondaryAuthors = authors => authors.map(author =>
  [author.lastName, author.firstName].filter(x => x).join(', '));

export function getAuthors(authors) {
  if (!Array.isArray(authors) || !authors.length) return {};

  const primary = authors[0];
  const isInitial = str => (/^([A-Z])\.?$/i).test(str);
  const firstNameFieldName = isInitial(primary.firstName)
    ? 'rft.auinit1' : 'rft.aufirst';

  const result = {
    // primary author
    'rft.aulast': primary.lastName,
    [firstNameFieldName]: primary.firstName,
    // secondary authors
    'rft.au': (authors.length > 1 ? getSecondaryAuthors(authors.slice(1)) : undefined)
  };

  return trimPropsWithUndefinedValues(result);
}

export function getIdentifierValue(doc, field) {
  return doc[field]
    ? doc[field]
    : doc.identifiers && doc.identifiers[field];
}

export function getIdentifiers(doc) {
  const idsForMainKey = ['doi', 'pmid'].reduce((ids, field) => {
    const value = getIdentifierValue(doc, field);
    if (value) {
      const idString = `info:${field}/${value}`;
      ids.push(idString);
    }
    return ids;
  }, []);

  const identifiers = {
    rft_id: idsForMainKey.length ? idsForMainKey : undefined,
    'rft.issn': getIdentifierValue(doc, 'issn'),
    'rft.isbn': getIdentifierValue(doc, 'isbn'),
  };

  return trimPropsWithUndefinedValues(identifiers);
}

export function getContextObject(doc, author) {
  const docType = getDocumentType(doc);

  const meta = {
    url_ver: 'Z39.88-2004', // OpenURL 1.0 version for URL
    ctx_ver: 'Z39.88-2004', // OpenURL 1.0 version for ContextObject
    rfr_id: `info:sid/${author}`, // referrer ID
    url_ctx_fmt: 'info:ofi/fmt:kev:mtx:ctx'
  };

  return {
    ...meta,
    ...getGenres(docType),
    ...getMappedFields(doc, docType),
    ...getAuthors(doc.authors),
    ...getIdentifiers(doc)
  };
}

export const generateOpenUrl = ({ baseUrl, doc, author = 'mendeley.com/mendeley' }) => {
  const query = queryString.stringify(getContextObject(doc, author));
  return `${baseUrl}?${query}`;
};



// WEBPACK FOOTER //
// ./src/universal/ui/utils/open-url/index.js